<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h1 class="mb-5">
          Подтверждение выполненных работ
        </h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        lg="3"
      >
        <v-select
          v-model="selectedPlatforms"
          :items="platformOptions"
          label="Платформа"
          item-text="name"
          item-value="id"
          outlined
          multiple
          small-chips
          clearable
          prepend-icon="mdi-home"
          hide-details
          :loading="isApplicationsLoading"
        />
        <v-btn
          text
          x-small
          color="primary"
          class="select-all-under-field px-0 mt-1"
          :disabled="!canSelectAllPlatforms"
          @click="selectAllPlatforms"
        >
          Выбрать все
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-data-table
          :headers="tableHeaders"
          :items="filteredApplications"
          single-expand
          :expanded.sync="expanded"
          item-key="id"
          show-expand
          class="elevation-1"
          hide-default-footer
          disable-pagination
          :loading="isApplicationsLoading"
        >
          <template #expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              <v-simple-table class="my-5">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">
                        Тип операции
                      </th>
                      <th class="text-left">
                        График
                      </th>
                      <th class="text-left">
                        План. объем
                      </th>
                      <th class="text-left">
                        Факт. объем
                      </th>
                      <th class="text-left">
                        ед.измерения
                      </th>
                      <th class="text-left">
                        План. количество людей
                      </th>
                      <th class="text-left">
                        Факт. количество людей
                      </th>
                      <th class="text-left">
                        Время работы
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(operation, index) in item.operationWorks"
                      :key="index"
                    >
                      <td>{{ operation.workType && operation.workType.name }}</td>
                      <td>{{ $t(operation.time_interval) }}</td>
                      <td>{{ operation.size }}</td>
                      <td>{{ operation.actual_size }}</td>
                      <td>{{ operation.units.units }}</td>
                      <td>{{ operation.quantity }}</td>
                      <td>{{ operation.actual_quantity }}</td>
                      <td>{{ operation.actual_work_time }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </td>
          </template>
          <template #item.date_from="{ value }">
            {{ formatDateForHumanSpecies(value) }}
          </template>
          <template #item.date_to="{ value }">
            {{ formatDateForHumanSpecies(value) }}
          </template>
          <template #item.confirm="{ item }">
            <v-btn
              color="primary"
              @click="confirm(item.id)"
            >
              <v-icon
                right
                dark
                class="mr-3"
              >
                mdi-check
              </v-icon>
              Подтвердить
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { Mixins } from 'vue-property-decorator';
import Component from 'vue-class-component';
import GetStatusApplicationListing from '@/graphql/GetStatusApplicationListing.gql';
import UserMixin from '@/mixins/UserMixin';
import { formatDateForHumanSpecies } from '@/utils/time';
import { processSupplier } from '@/utils/config';
import Axios from 'axios';
import EntityHelperMixin from '@/mixins/EntityHelperMixin';

@Component({})
export default class ConfirmationOfCompletedWork extends Mixins(UserMixin, EntityHelperMixin) {
  isApplicationsLoading = false;

  tableHeaders = [
    { text: 'ID', value: 'id' },
    { text: 'Платформа', value: 'platform.name' },
    { text: 'Дата, с', value: 'date_from' },
    { text: 'Дата, до', value: 'date_to' },
    { text: 'Координатор', value: 'coordinator.full_name' },
    { text: 'Клиент', value: 'client.legal_name' },
    { text: '', value: 'confirm' },
  ]

  applications = []

  selectedPlatforms = []

  statusId = null

  expanded = []

  /**
   * Платформы для фильтра — из уже загруженных заявок поставщика: это ровно те площадки,
   * по которым ему есть что подтверждать. Справочник PlantsHR целиком брать нельзя,
   * он отдаёт все платформы системы.
   */
  get platformOptions() {
    const options = new Map();
    this.applications.forEach((application) => {
      const platform = application?.platform;
      if (!platform?.id) {
        return;
      }
      options.set(String(platform.id), { id: String(platform.id), name: platform.name || String(platform.id) });
    });
    return [...options.values()].sort((a, b) => a.name.localeCompare(b.name));
  }

  get filteredApplications() {
    if (!this.selectedPlatforms.length) {
      return this.applications;
    }
    const selected = new Set(this.selectedPlatforms.map(String));
    return this.applications.filter((application) => selected.has(String(application?.platform?.id)));
  }

  get canSelectAllPlatforms() {
    return this.platformOptions.length > 0
      && this.selectedPlatforms.length !== this.platformOptions.length;
  }

  selectAllPlatforms() {
    this.selectedPlatforms = this.platformOptions.map((platform) => platform.id);
  }

  /** После подтверждения заявка уходит из выборки — платформа могла исчезнуть вместе с ней. */
  dropUnavailablePlatformsFromFilter() {
    if (!this.selectedPlatforms.length) {
      return;
    }
    const available = new Set(this.platformOptions.map((platform) => platform.id));
    this.selectedPlatforms = this.selectedPlatforms.filter((id) => available.has(String(id)));
  }

  async beforeMount() {
    await this.loadStatusId();
    await this.loadApplications();
  }

  formatDateForHumanSpecies(value) {
    return formatDateForHumanSpecies(value);
  }

  async confirm(id) {
    const url = processSupplier.confirmFact;
    try {
      await Axios.put(url, { applicationId: id });
    } catch (e) {
      console.error(e);
    }
    await this.loadApplications();
  }

  async loadStatusId() {
    return this.$apollo.query({
      query: GetStatusApplicationListing,
    }).then((res) => {
      if (res?.data?.getStatusApplicationListing?.edges?.length) {
        const status = res?.data?.getStatusApplicationListing?.edges.find((item) => item.node.code === 'actual_data_filled');
        if (status) {
          this.statusId = Number(status.node.id) || null;
          return status;
        }
      }
      return null;
    }).catch((e) => {
      console.error(e);
    });
  }

  async loadApplications() {
    if (this.pimcoreUser?.supplier_id && this.statusId) {
      this.isApplicationsLoading = true;
      const filter = {
        $and: [
          {
            Supplier__id: {
              $like: this.pimcoreUser.supplier_id,
            },
          },
          {
            status__id: {
              $like: this.statusId,
            },
          },
        ],
      };
      try {
        const applications = await this.loadEntityListing('ApplicationHR', { filter: JSON.stringify(filter) }, 'network-only', { maxDepth: 3 });
        if (applications?.length) {
          this.applications = applications;
        } else {
          this.applications = [];
        }
        this.dropUnavailablePlatformsFromFilter();
        this.isApplicationsLoading = false;
        return applications;
      } catch (e) {
        console.error(e);
        this.isApplicationsLoading = false;
      }
    }
    return null;
  }
}
</script>

<style scoped>
.select-all-under-field {
  display: block;
  margin-left: 56px;
}
</style>
